import React, { useMemo, useState } from 'react';
import { useCombobox } from 'downshift';
import { Box, List, ListItem } from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { Input } from '../UI-CHAKRA/Input/Input';
import { InputGroup } from '../UI-CHAKRA/Input/InputGroup';
import { InputRightElement } from '../UI-CHAKRA/Input/InputRightElement';
import { Text } from '../UI-CHAKRA/Text';
import { Spinner } from '../UI-CHAKRA/Spinner';
import { FormLabel } from '../UI-CHAKRA/FormLabel';
import { filterSpecialtyOptions } from '../../helpers/rqeSpecialties';

interface FreeSampleSpecialtyComboboxProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  // Fallback de RQE em andamento: o valor pré-preenchido ainda não chegou.
  isLoading?: boolean;
}

// Especialidade do médico no fluxo de amostra grátis: só aceita uma opção da tabela de-para de RQE
// (ou "SEM ESPECIALIDADE"). Digitar filtra a lista; sair do campo sem escolher volta pro valor
// selecionado — o texto digitado nunca vira especialidade.
const FreeSampleSpecialtyCombobox: React.FC<FreeSampleSpecialtyComboboxProps> = ({
  label,
  value,
  onChange,
  onBlur,
  isLoading = false,
}) => {
  const [query, setQuery] = useState('');
  const items = useMemo(() => filterSpecialtyOptions(query), [query]);

  const { isOpen, highlightedIndex, getLabelProps, getInputProps, getMenuProps, getItemProps, getToggleButtonProps } =
    useCombobox<string>({
      items,
      selectedItem: value || null,
      itemToString: (item) => item ?? '',
      onSelectedItemChange: ({ selectedItem }) => {
        if (selectedItem) onChange(selectedItem);
      },
      onInputValueChange: ({ type, inputValue }) => {
        if (type === useCombobox.stateChangeTypes.InputChange) setQuery(inputValue ?? '');
      },
      onIsOpenChange: ({ isOpen: open }) => {
        if (!open) setQuery('');
      },
      // Por padrão o downshift seleciona o item destacado ao perder o foco; aqui sair do campo só
      // descarta o que foi digitado.
      stateReducer: (state, { type, changes }) => {
        if (type === useCombobox.stateChangeTypes.InputBlur) {
          return { ...changes, selectedItem: state.selectedItem, inputValue: state.selectedItem ?? '' };
        }
        return changes;
      },
    });

  return (
    <Box position={'relative'} w={'100%'}>
      <FormLabel {...getLabelProps()}>{label}</FormLabel>
      <InputGroup>
        <Input
          pr={'36px'}
          {...getInputProps({
            onBlur,
            onFocus: (e: React.FocusEvent<HTMLInputElement>) => e.target.select(),
            disabled: isLoading,
          })}
          placeholder={isLoading ? 'Carregando especialidade...' : 'Selecione sua especialidade'}
        />
        <InputRightElement>
          {isLoading ? (
            <Spinner size={'sm'} color={'primary.main'} />
          ) : (
            <Box
              as="button"
              type="button"
              aria-label={'Ver especialidades'}
              display={'flex'}
              {...getToggleButtonProps()}
            >
              <ChevronDownIcon boxSize={'20px'} color={'typography.dark'} />
            </Box>
          )}
        </InputRightElement>
      </InputGroup>

      <List
        {...getMenuProps()}
        display={isOpen ? 'block' : 'none'}
        position={'absolute'}
        zIndex={1000}
        w={'100%'}
        maxH={'200px'}
        overflowY={'auto'}
        mt={'3px'}
        bg={'#fff'}
        border={'1px solid'}
        borderColor={'#DEE2E6'}
        borderRadius={'10px'}
      >
        {isOpen &&
          items.map((item, index) => (
            <ListItem
              key={item}
              {...getItemProps({ item, index })}
              padding={'0.4rem 0.8rem'}
              cursor={'pointer'}
              bg={highlightedIndex === index ? '#F1F3F5' : 'white'}
              fontWeight={item === value ? 700 : 400}
            >
              <Text fontSize={'14px'}>{item}</Text>
            </ListItem>
          ))}
        {isOpen && items.length === 0 && (
          <ListItem padding={'0.4rem 0.8rem'}>
            <Text fontSize={'14px'} color={'#6C757D'}>
              Nenhuma especialidade encontrada
            </Text>
          </ListItem>
        )}
      </List>
    </Box>
  );
};

export default FreeSampleSpecialtyCombobox;
